import React, { useId } from "react";
import { cn } from "@/lib/utils";

/** Label + control + optional hint. Passes a generated id to its child control. */
export function Field({ label, hint, className, children }) {
  const id = useId();
  const controlId = children?.props?.id || id;
  const control = React.isValidElement(children) ? React.cloneElement(children, { id: controlId }) : children;
  return (
    <div className={className}>
      {label && <label htmlFor={controlId} className="label">{label}</label>}
      {control}
      {hint && <p className="text-[11px] text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className, icon: Icon, ...rest }, ref) {
  if (!Icon) return <input ref={ref} className={cn("input", className)} {...rest} />;
  return (
    <div className="relative">
      <Icon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input ref={ref} className={cn("input pl-10", className)} {...rest} />
    </div>
  );
});

export function Textarea({ className, rows = 4, ...rest }) {
  return <textarea rows={rows} className={cn("input resize-y", className)} {...rest} />;
}

/** Native select in the recessed input style. `options` = strings or {value, label}. */
export function Select({ options = [], placeholder, className, ...rest }) {
  return (
    <select className={cn("input cursor-pointer", className)} {...rest}>
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map((o) => {
        const { value, label } = typeof o === "string" ? { value: o, label: o } : o;
        return <option key={value} value={value}>{label}</option>;
      })}
    </select>
  );
}

/** Admin-panel Toggle: orange track when on, recessed track when off. */
export function Switch({ checked, onChange, disabled, label, size = "md" }) {
  const w = size === "sm" ? 38 : 46, h = size === "sm" ? 20 : 24, thumb = size === "sm" ? 14 : 18, off = 3;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={cn(
        "relative rounded-full transition-all duration-200 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/50",
        checked && "bg-brand-orange",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      )}
      style={{
        width: w, height: h, minWidth: w,
        ...(checked
          ? { boxShadow: "inset 2px 2px 5px rgba(0,0,0,0.20), inset -2px -2px 5px rgba(255,255,255,0.20)" }
          : { background: "#EEF2F8", boxShadow: "inset 4px 4px 9px rgba(166,180,200,0.50), inset -3px -3px 9px rgba(255,255,255,1)" }),
      }}
    >
      <span
        className="absolute bg-white rounded-full transition-all duration-200"
        style={{ width: thumb, height: thumb, top: off, left: checked ? w - thumb - off : off, boxShadow: "2px 2px 5px rgba(0,0,0,0.22), -1px -1px 3px rgba(255,255,255,0.95)" }}
      />
    </button>
  );
}

const TAB_ACTIVE = {
  blue: "linear-gradient(145deg,#225881,#1a4363)",
  green: "linear-gradient(145deg,#16a34a,#15803d)",
  orange: "linear-gradient(145deg,#ea580c,#c2410c)",
  red: "linear-gradient(145deg,#dc2626,#b91c1c)",
  slate: "linear-gradient(145deg,#374151,#1f2937)",
};

/**
 * Pill tabs (admin panel StatusTabs). options: strings or {value, label, count?, tone?}.
 * `tone` picks the active gradient — blue (default), green, orange, red, slate.
 */
export function Segmented({ value, onChange, options, className }) {
  return (
    <div role="tablist" className={cn("flex gap-2.5 overflow-x-auto no-scrollbar p-1 -m-1 pb-3", className)}>
      {options.map((o) => {
        const { value: v, label, count, tone = "blue" } = typeof o === "string" ? { value: o, label: o } : o;
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v)}
            className={cn("flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200", active ? "text-white" : "text-slate-600 shadow-neu-xs")}
            style={active ? { background: TAB_ACTIVE[tone], boxShadow: "inset 3px 3px 7px rgba(18,42,70,0.45), inset -3px -3px 7px rgba(255,255,255,0.18)" } : { background: "#EEF2F8" }}
          >
            {label}
            {count > 0 && (
              <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-extrabold leading-none", active ? "bg-white/20 text-white" : "bg-surface-200 text-brand-blue")}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
